@let hasShiftUpdatePermission =
  can(RoleAction.Update, RolePermissionSubject.Shift);
@let hasShiftReadPermission = can(RoleAction.Read, RolePermissionSubject.Shift);
@let hasShiftAttachmentReadPermission =
  can(RoleAction.Read, RolePermissionSubject.ShiftAttachment);
@let hasPayrollReadPermission =
  can(RoleAction.Read, RolePermissionSubject.Payroll);
@let forbiddenMessage = 'general.forbidden_action' | translate;
@let hasServiceUpdatePermission =
  can(RoleAction.Update, RolePermissionSubject.Service);
@let hasCirculationGroupReadPermission =
  can(RoleAction.Read, RolePermissionSubject.CirculationGroup);
@let hasShiftEditPermission =
  hasServiceActivitySignal()
    ? hasShiftUpdatePermission && hasServiceUpdatePermission
    : hasShiftUpdatePermission;
@let selectedShift = selectedShiftLinkedSignal();
@let isLoadingSelectedShift = isLoadingSelectedShift$ | async;
@let shiftDrawerCollapseSettings = shiftDrawerCollapseSettings$ | async;
@let isFormPristine = !!(shiftDrawerCloseHelperService.isFormPristine$ | async);
@let isAuditTrailEnabled =
  !!('audit-trail-global' | featureFlagPure | async) &&
  !!('audit-trail-shift' | featureFlagPure | async);

@if (isDrawerVisible()) {
  <nz-drawer
    wilsonDrawerSettingsManager
    [drawerId]="drawerId"
    [nzSize]="'large'"
    [nzVisible]="true"
    [nzMask]="false"
    [nzFooter]="drawerFooter"
    [nzExtra]="extraTemplate"
    [nzClosable]="true"
    [nzCloseIcon]="isFormPristine ? 'close' : ''"
    [nzMaskClosable]="false"
    (nzOnClose)="closeDrawer.emit()"
  >
    <ng-container *nzDrawerContent>
      <wilson-nz-drawer-content [drawerId]="drawerId" [nzSize]="'large'">
        @if (isLoadingSelectedShift) {
          <div>
            <nz-skeleton
              [nzActive]="true"
              [nzParagraph]="{ rows: 14 }"
            ></nz-skeleton>
          </div>
        } @else {
          @if (selectedShift) {
            <div>
              <div class="shift-status">
                <wilson-shift-status
                  [shift]="selectedShift"
                ></wilson-shift-status>
              </div>
              <wilson-collapsible-shift-detail
                [isExpandedOnOpen]="
                  !!shiftDrawerCollapseSettings?.[
                    ShiftDrawerCollapseSectionEnum.ShiftDetails
                  ]
                "
                [isLoadingSelectedShift]="isLoadingSelectedShift ?? false"
                [selectedShift]="selectedShift"
                [resetForm]="resetFormNotifier$ | async"
                (isInvalid)="isFormInvalid.set($event)"
                (isPristine)="setIsFormPristine($event)"
                (value)="updateShiftDetails(selectedShift, $event)"
                (collapseChange)="
                  onCollapseChange(
                    $event,
                    ShiftDrawerCollapseSectionEnum.ShiftDetails
                  )
                "
              ></wilson-collapsible-shift-detail>
              <nz-divider class="my-0"></nz-divider>
              @defer {
                <wilson-collapsible-shift-content
                  [isExpandedOnOpen]="
                    !!shiftDrawerCollapseSettings?.[
                      ShiftDrawerCollapseSectionEnum.ShiftContents
                    ]
                  "
                  [shiftWithActivities]="selectedShift"
                  (isShiftUpdateOperationOngoing)="
                    isShiftUpdateOperationOngoingSignal.set($event)
                  "
                  (updateSuccess)="
                    updateShiftPublicationStatusAndReloadDrawerAndTimeline(
                      selectedShift
                    )
                  "
                  (collapseChange)="
                    onCollapseChange(
                      $event,
                      ShiftDrawerCollapseSectionEnum.ShiftContents
                    )
                  "
                ></wilson-collapsible-shift-content>
              } @placeholder {
                <nz-skeleton
                  [nzActive]="true"
                  [nzParagraph]="{ rows: 1 }"
                  [nzTitle]="false"
                ></nz-skeleton>
              }
              @defer {
                <wilson-collapsible-activities-list
                  [isExpandedOnOpen]="
                    !!shiftDrawerCollapseSettings?.[
                      ShiftDrawerCollapseSectionEnum.Activities
                    ]
                  "
                  [shift]="selectedShift"
                  (refreshTableDataAndUpdateShiftPublication)="
                    updateShiftPublicationStatusAndReloadDrawerAndTimeline(
                      selectedShift
                    )
                  "
                  (delete)="deleteActivity($event)"
                  (collapseChange)="
                    onCollapseChange(
                      $event,
                      ShiftDrawerCollapseSectionEnum.Activities
                    )
                  "
                ></wilson-collapsible-activities-list>
              } @placeholder {
                <nz-skeleton
                  [nzActive]="true"
                  [nzParagraph]="{ rows: 1 }"
                  [nzTitle]="false"
                ></nz-skeleton>
              }
              <nz-divider class="my-0"></nz-divider>
              @if (
                hasCirculationGroupReadPermission &&
                ('portal-circulation-grouping' | featureFlagPure | async)
              ) {
                <wilson-collapsible-grouping-section
                  [drawerItemId]="selectedShift.id"
                ></wilson-collapsible-grouping-section>
              }
              <nz-divider class="my-0"></nz-divider>

              @if (
                hasShiftAttachmentReadPermission &&
                ('shift-attachments' | featureFlagPure | async)
              ) {
                <wilson-collapsible-shift-attachments
                  [selectedShift]="selectedShift"
                  [isExpandedOnOpen]="false"
                ></wilson-collapsible-shift-attachments>
              }
              @if (
                'approved-time-report-attachments' | featureFlagPure | async
              ) {
                <wilson-collapsible-shift-reports-attachments
                  [selectedShift]="selectedShift"
                  [isExpandedOnOpen]="false"
                ></wilson-collapsible-shift-reports-attachments>
              }
              <wilson-collapsible-shift-assigned-user-identity
                [isExpandedOnOpen]="
                  !!shiftDrawerCollapseSettings?.[
                    ShiftDrawerCollapseSectionEnum.Staff
                  ]
                "
                [user]="selectedShift?.user"
                [renderReassignButton]="true"
                (clicked)="openDriverSearch()"
                (collapseChange)="
                  onCollapseChange($event, ShiftDrawerCollapseSectionEnum.Staff)
                "
              >
                @if (selectedShift) {
                  <wilson-shift-last-seen
                    [payload]="selectedShift"
                  ></wilson-shift-last-seen>
                  <wilson-shift-confirmed-declined
                    [payload]="selectedShift"
                  ></wilson-shift-confirmed-declined>
                }
              </wilson-collapsible-shift-assigned-user-identity>
              <nz-divider class="my-0"></nz-divider>
              @if (FeatureName.Payroll | featurePure | async) {
                <wilson-collapsible-shift-wage-data
                  [shift]="selectedShift"
                  [isExpandedOnOpen]="
                    !!shiftDrawerCollapseSettings?.[
                      ShiftDrawerCollapseSectionEnum.WageData
                    ]
                  "
                  [areInputsDisabled]="
                    selectedShift?.state === ShiftState.AcceptedTimes ||
                    selectedShift?.state ===
                      ShiftState.SubmittedToPayrollProvider
                  "
                  (collapseChange)="
                    onCollapseChange(
                      $event,
                      ShiftDrawerCollapseSectionEnum.WageData
                    )
                  "
                ></wilson-collapsible-shift-wage-data>
                <nz-divider class="my-0"></nz-divider>
                @if (
                  hasPayrollReadPermission &&
                  selectedShift &&
                  (selectedShift?.state | isShiftSubmittedOrAccepted)
                ) {
                  <div>
                    <wilson-payroll-transactions
                      [userId]="selectedShift.userId || undefined"
                      [shiftId]="selectedShift.id"
                      [shiftDate]="selectedShift.startDate"
                      [shiftState]="selectedShift.state"
                      [isExpandedOnOpen]="
                        !!shiftDrawerCollapseSettings?.[
                          ShiftDrawerCollapseSectionEnum.PayrollTransactions
                        ]
                      "
                      (collapseChange)="
                        onCollapseChange(
                          $event,
                          ShiftDrawerCollapseSectionEnum.PayrollTransactions
                        )
                      "
                    ></wilson-payroll-transactions>
                    <nz-divider class="my-0"></nz-divider>
                  </div>
                }
                @if (hasShiftUpdatePermission) {
                  <wilson-collapsible-shift-note
                    [shiftId]="selectedShift?.id"
                    [isExpandedOnOpen]="
                      !!shiftDrawerCollapseSettings?.[
                        ShiftDrawerCollapseSectionEnum.PayrollNote
                      ]
                    "
                    (collapseChange)="
                      onCollapseChange(
                        $event,
                        ShiftDrawerCollapseSectionEnum.PayrollNote
                      )
                    "
                    [payrollInformation]="selectedShift?.payrollInformation"
                  ></wilson-collapsible-shift-note>
                }
              }
              <wilson-collapsible-shift-quick-check
                [userId]="selectedShift?.user?.id"
                [shiftValidationDetails]="
                  (shiftQuickCheckService.shiftValidation$ | async) || []
                "
                [isLoading]="
                  !!(shiftQuickCheckService.isLoadingValidation$ | async)
                "
                [isExpandedOnOpen]="
                  !!shiftDrawerCollapseSettings?.[
                    ShiftDrawerCollapseSectionEnum.QuickCheck
                  ]
                "
                (collapseChange)="
                  onCollapseChange(
                    $event,
                    ShiftDrawerCollapseSectionEnum.QuickCheck
                  )
                "
              ></wilson-collapsible-shift-quick-check>
              <nz-divider class="my-0"></nz-divider>
              <wilson-collapsible-shift-further-information
                [isExpandedOnOpen]="
                  !!shiftDrawerCollapseSettings?.[
                    ShiftDrawerCollapseSectionEnum.ShiftFeatures
                  ]
                "
                [shift]="selectedShift"
                [currentShiftNecessaryStay]="
                  !!selectedShift?.ignoreStayNecessary
                "
                (reloadValidations)="
                  reloadShiftAndUpdateTimeline(selectedShift.id)
                "
                (updatedNecessaryStay)="updateShiftNecessaryStay($event)"
                (collapseChange)="
                  onCollapseChange(
                    $event,
                    ShiftDrawerCollapseSectionEnum.ShiftFeatures
                  )
                "
              ></wilson-collapsible-shift-further-information>
            </div>
          } @else {
            <div>
              {{ 'general.error' | translate }}
            </div>
          }
        }
      </wilson-nz-drawer-content>
    </ng-container>

    <ng-template #drawerFooter>
      <div class="drawer__footer">
        <wilson-last-update-status
          [updatedAt]="updatedAtSignal()"
          [isUpdated]="isSuccessfullySavedSignal()"
          [isClickable]="isAuditTrailEnabled"
          (clickEvent)="toggleChangeHistoryDrawer()"
        ></wilson-last-update-status>
      </div>
    </ng-template>

    <ng-template #extraTemplate>
      <wilson-nz-drawer-header [drawerId]="drawerId">
        @if (shiftViewers()?.length) {
          <wilson-viewers-avatars
            drawerHeaderStart
            class="me-2"
            [viewers]="shiftViewers() ?? []"
            [tooltipTranslationKey]="
              'page.shift_timeline.presence.shift_viewers_label'
            "
          ></wilson-viewers-avatars>
        }

        <div class="drawer-extra">
          @if (selectedShift && !isLoadingSelectedShift) {
            <wilson-shift-form-view-button
              class="me-2"
              nz-tooltip
              [nzTooltipTitle]="
                !hasShiftReadPermission
                  ? forbiddenMessage
                  : ('page.shift_timeline.drawer.shift_form.view_shift'
                    | translate)
              "
              nzTooltipPlacement="bottom"
              [shiftId]="selectedShift.id"
              [disabled]="
                !hasShiftReadPermission || isShiftUpdateOperationOngoingSignal()
              "
              [isFormPristine]="isFormPristine"
              data-test-id="wilson-timeline-drawer:view-shift"
            ></wilson-shift-form-view-button>

            <wilson-more-shift-actions-dropdown
              class="me-2"
              [shift]="selectedShift"
              [renderMoveOption]="true"
              [isMoveAllowed]="[selectedShift] | canAllShiftBeModified"
              [disableSaveAsTemplate]="isFormInvalid()"
              [duplicateShiftInNewTab]="true"
              [disabled]="isShiftUpdateOperationOngoingSignal()"
              [showIconOnly]="true"
              (convertShiftToStandbySuccessful)="
                reloadShiftAndUpdateTimeline(selectedShift.id, true)
              "
              (deleteTriggered)="deletionConfirmed.emit(selectedShift)"
              (filledShiftWithPlannedTimeSuccessful)="
                reloadShiftAndUpdateTimeline(selectedShift.id, true)
              "
              (moveShiftSuccess)="
                reloadShiftAndUpdateTimeline(selectedShift.id, true)
              "
            ></wilson-more-shift-actions-dropdown>

            @let areAllActivitiesFinished =
              selectedShift.activities | areAllActivitiesFinished;
            @if (!areAllActivitiesFinished) {
              <wilson-shift-form-edit-button
                class="me-2"
                nz-tooltip
                [nzTooltipTitle]="
                  !hasShiftEditPermission
                    ? forbiddenMessage
                    : ('page.shift_timeline.drawer.shift_form.edit_shift'
                      | translate)
                "
                nzTooltipPlacement="bottom"
                [shiftId]="selectedShift.id"
                [isFormPristine]="isFormPristine"
                [activities]="selectedShift.activities"
                [disabled]="
                  !hasShiftEditPermission ||
                  isShiftUpdateOperationOngoingSignal() ||
                  areAllActivitiesFinished
                "
                data-test-id="wilson-timeline-drawer:edit-shift"
              ></wilson-shift-form-edit-button>
            }
            @if (
              selectedShift?.publicationStatus !== PublicationStatus.Published
            ) {
              <wilson-shift-form-publish-button
                nz-tooltip
                [nzTooltipTitle]="
                  !hasShiftUpdatePermission ? forbiddenMessage : null
                "
                [selectedShift]="selectedShift"
                (reloadDataEvent)="
                  reloadShiftAndUpdateTimeline(selectedShift.id)
                "
                [disabled]="
                  !hasShiftUpdatePermission ||
                  isShiftUpdateOperationOngoingSignal()
                "
                data-test-id="wilson-timeline-drawer:publish-shift"
              ></wilson-shift-form-publish-button>
            }

            <wilson-revert-shift-button
              [shift]="selectedShift"
              [disabled]="isShiftUpdateOperationOngoingSignal()"
              [showIconOnly]="true"
              (updateSuccess)="reloadShiftAndUpdateTimeline(selectedShift.id)"
              data-test-id="wilson-timeline-drawer:revert-shift"
            ></wilson-revert-shift-button>

            <wilson-send-shift-back-to-assignee-button
              class="me-2"
              nz-tooltip
              [nzTooltipTitle]="
                !hasShiftUpdatePermission ? forbiddenMessage : null
              "
              [shift]="selectedShift"
              [disabled]="
                !hasShiftUpdatePermission ||
                isShiftUpdateOperationOngoingSignal()
              "
              [showIconOnly]="true"
              (updateSuccess)="reloadShiftAndUpdateTimeline(selectedShift.id)"
              data-test-id="wilson-timeline-drawer:send-back-to-assignee"
            ></wilson-send-shift-back-to-assignee-button>

            <wilson-accept-shift-button
              nz-tooltip
              [nzTooltipTitle]="
                !hasShiftUpdatePermission ? forbiddenMessage : null
              "
              [shift]="selectedShift"
              [disabled]="
                !hasShiftUpdatePermission ||
                isShiftUpdateOperationOngoingSignal()
              "
              (updateSuccess)="reloadShiftAndUpdateTimeline(selectedShift.id)"
              [textOnly]="true"
              data-test-id="wilson-timeline-drawer:accept-shift"
            ></wilson-accept-shift-button>
          }
        </div>
      </wilson-nz-drawer-header>
    </ng-template>

    @if (isAuditTrailEnabled) {
      <wilson-audit-trail-drawer></wilson-audit-trail-drawer>
    }
  </nz-drawer>
}
